/**
 * LabourRatesBankView.js
 *
 * Beautiful consolidated labour rates bank dashboard with section tabs,
 * summary cards, division/project filters, and inline editing.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';

const SECTION_CONFIG = {
  'Working Week - Pre-Site': { icon: '🏗️', color: '#2563eb', shortLabel: 'Pre-Site', gradient: 'linear-gradient(135deg, #2563eb, #1d4ed8)' },
  'Working Week - At Site': { icon: '⚙️', color: '#059669', shortLabel: 'At Site', gradient: 'linear-gradient(135deg, #059669, #047857)' },
  'Working Week - Demobilization': { icon: '🚚', color: '#7c3aed', shortLabel: 'Demob', gradient: 'linear-gradient(135deg, #7c3aed, #6d28d9)' },
  'Phases Blending Ratios': { icon: '📊', color: '#d97706', shortLabel: 'Blending', gradient: 'linear-gradient(135deg, #d97706, #b45309)' },
  'Project Support Staff': { icon: '👥', color: '#dc2626', shortLabel: 'Support Staff', gradient: 'linear-gradient(135deg, #dc2626, #b91c1c)' },
  'Field Supervision': { icon: '👷', color: '#0891b2', shortLabel: 'Supervision', gradient: 'linear-gradient(135deg, #0891b2, #0e7490)' },
  'Field Labour': { icon: '🔧', color: '#4f46e5', shortLabel: 'Field Labour', gradient: 'linear-gradient(135deg, #4f46e5, #4338ca)' },
  'Shop Labour': { icon: '🏭', color: '#be185d', shortLabel: 'Shop Labour', gradient: 'linear-gradient(135deg, #be185d, #9d174d)' },
  'R&B Trades - Hourly': { icon: '🏨', color: '#ea580c', shortLabel: 'R&B Trades', gradient: 'linear-gradient(135deg, #ea580c, #c2410c)' },
  'R&B Foremen & GF - Hourly': { icon: '🏠', color: '#16a34a', shortLabel: 'R&B Foremen', gradient: 'linear-gradient(135deg, #16a34a, #15803d)' },
  'R&B Staff - Weekly': { icon: '📋', color: '#8b5cf6', shortLabel: 'R&B Staff', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' },
};

const SECTION_COLUMNS = {
  'Working Week - Pre-Site': [
    { key: 'account_no', label: 'Account #', width: 110, type: 'text' },
    { key: 'description', label: 'Trade', width: 180, type: 'text' },
    { key: 'division', label: 'Division', width: 120, type: 'text' },
    { key: 'project', label: 'Project', width: 140, type: 'text' },
    { key: 'ds_work_sched', label: 'DS Schedule', width: 110, type: 'text', group: 'Day Shift' },
    { key: 'ds_nb_days_wk', label: 'DS Days/Wk', width: 95, type: 'number', group: 'Day Shift' },
    { key: 'ds_straight_time', label: 'DS Straight', width: 100, type: 'number', group: 'Day Shift' },
    { key: 'ds_overtime_t15', label: 'DS OT (1½)', width: 100, type: 'number', group: 'Day Shift' },
    { key: 'ds_double_time', label: 'DS Double', width: 100, type: 'number', group: 'Day Shift' },
    { key: 'ds_total_working_week', label: 'DS Total Wk', width: 105, type: 'number', group: 'Day Shift' },
    { key: 'ds_addl_time_supervisor', label: 'DS Addl Super', width: 110, type: 'number', group: 'Day Shift' },
    { key: 'ds_addl_time_fore_gf', label: 'DS Addl Fore/GF', width: 115, type: 'number', group: 'Day Shift' },
    { key: 'ds_notes', label: 'DS Notes', width: 180, type: 'text', group: 'Day Shift' },
    { key: 'ns_work_sched', label: 'NS Schedule', width: 110, type: 'text', group: 'Night Shift' },
    { key: 'ns_nb_days_wk', label: 'NS Days/Wk', width: 95, type: 'number', group: 'Night Shift' },
    { key: 'ns_straight_time', label: 'NS Straight', width: 100, type: 'number', group: 'Night Shift' },
    { key: 'ns_overtime_t15', label: 'NS OT (1½)', width: 100, type: 'number', group: 'Night Shift' },
    { key: 'ns_double_time', label: 'NS Double', width: 100, type: 'number', group: 'Night Shift' },
    { key: 'ns_total_working_week', label: 'NS Total Wk', width: 105, type: 'number', group: 'Night Shift' },
    { key: 'ns_notes', label: 'NS Notes', width: 180, type: 'text', group: 'Night Shift' },
  ],
  'Phases Blending Ratios': [
    { key: 'account_no', label: 'Account #', width: 110, type: 'text' },
    { key: 'description', label: 'Trade', width: 180, type: 'text' },
    { key: 'division', label: 'Division', width: 120, type: 'text' },
    { key: 'project', label: 'Project', width: 140, type: 'text' },
    { key: 'ds_total_check', label: 'DS Total Check', width: 120, type: 'number', group: 'Day Shift' },
    { key: 'ds_pre_site_ratio', label: 'DS Pre-Site', width: 100, type: 'number', group: 'Day Shift' },
    { key: 'ds_site_ratio', label: 'DS Site', width: 100, type: 'number', group: 'Day Shift' },
    { key: 'ds_post_site_ratio', label: 'DS Post-Site', width: 105, type: 'number', group: 'Day Shift' },
    { key: 'ds_notes', label: 'DS Notes', width: 200, type: 'text', group: 'Day Shift' },
    { key: 'ns_total_check', label: 'NS Total Check', width: 120, type: 'number', group: 'Night Shift' },
    { key: 'ns_pre_site_ratio', label: 'NS Pre-Site', width: 100, type: 'number', group: 'Night Shift' },
    { key: 'ns_site_ratio', label: 'NS Site', width: 100, type: 'number', group: 'Night Shift' },
    { key: 'ns_post_site_ratio', label: 'NS Post-Site', width: 105, type: 'number', group: 'Night Shift' },
    { key: 'ns_notes', label: 'NS Notes', width: 200, type: 'text', group: 'Night Shift' },
  ],
  'Project Support Staff': [
    { key: 'account_no', label: 'Account #', width: 100, type: 'text' },
    { key: 'description', label: 'Role', width: 260, type: 'text' },
    { key: 'trade', label: 'Trade', width: 130, type: 'text' },
    { key: 'division', label: 'Division', width: 120, type: 'text' },
    { key: 'project', label: 'Project', width: 140, type: 'text' },
    { key: 'ds_straight_time', label: 'DS Straight', width: 100, type: 'number', group: 'Day Shift' },
    { key: 'ds_overtime_t15', label: 'DS Premium 1½', width: 110, type: 'number', group: 'Day Shift' },
    { key: 'ds_double_time', label: 'DS Premium 2x', width: 110, type: 'number', group: 'Day Shift' },
    { key: 'ds_pre_site_blended', label: 'DS Pre-Site Blend', width: 130, type: 'number', group: 'Day Shift' },
    { key: 'ds_site_blended', label: 'DS Site Blend', width: 115, type: 'number', group: 'Day Shift' },
    { key: 'ds_post_site_blended', label: 'DS Post-Site Blend', width: 135, type: 'number', group: 'Day Shift' },
    { key: 'ds_notes', label: 'DS Notes', width: 180, type: 'text', group: 'Day Shift' },
    { key: 'ns_straight_time', label: 'NS Straight', width: 100, type: 'number', group: 'Night Shift' },
    { key: 'ns_overtime_t15', label: 'NS Premium 1½', width: 110, type: 'number', group: 'Night Shift' },
    { key: 'ns_double_time', label: 'NS Premium 2x', width: 110, type: 'number', group: 'Night Shift' },
    { key: 'ns_site_blended', label: 'NS Site Blend', width: 115, type: 'number', group: 'Night Shift' },
    { key: 'ns_notes', label: 'NS Notes', width: 180, type: 'text', group: 'Night Shift' },
  ],
  'R&B Trades - Hourly': [
    { key: 'description', label: 'Trade', width: 200, type: 'text' },
    { key: 'division', label: 'Division', width: 120, type: 'text' },
    { key: 'project', label: 'Project', width: 140, type: 'text' },
    { key: 'rb_ds_pre_site_days_wk', label: 'DS Pre-Site Days', width: 125, type: 'number', group: 'Day Shift' },
    { key: 'rb_ds_site_days_wk', label: 'DS Site Days', width: 110, type: 'number', group: 'Day Shift' },
    { key: 'rb_ds_post_site_days_wk', label: 'DS Post Days', width: 110, type: 'number', group: 'Day Shift' },
    { key: 'rb_ds_basic_daily', label: 'DS Basic Daily', width: 115, type: 'number', group: 'Day Shift' },
    { key: 'rb_ds_addl_daily', label: 'DS Addl Daily', width: 110, type: 'number', group: 'Day Shift' },
    { key: 'rb_ds_total_daily', label: 'DS Total Daily', width: 115, type: 'number', group: 'Day Shift' },
    { key: 'rb_ds_pre_site_hourly', label: 'DS Pre Hourly', width: 115, type: 'number', group: 'Day Shift' },
    { key: 'rb_ds_site_hourly', label: 'DS Site Hourly', width: 115, type: 'number', group: 'Day Shift' },
    { key: 'rb_ds_post_site_hourly', label: 'DS Post Hourly', width: 115, type: 'number', group: 'Day Shift' },
    { key: 'ds_notes', label: 'DS Notes', width: 220, type: 'text', group: 'Day Shift' },
    { key: 'rb_ns_site_days_wk', label: 'NS Site Days', width: 110, type: 'number', group: 'Night Shift' },
    { key: 'rb_ns_basic_daily', label: 'NS Basic Daily', width: 115, type: 'number', group: 'Night Shift' },
    { key: 'rb_ns_total_daily', label: 'NS Total Daily', width: 115, type: 'number', group: 'Night Shift' },
    { key: 'rb_ns_site_hourly', label: 'NS Site Hourly', width: 115, type: 'number', group: 'Night Shift' },
    { key: 'ns_notes', label: 'NS Notes', width: 200, type: 'text', group: 'Night Shift' },
  ],
  'R&B Staff - Weekly': [
    { key: 'rnb_code', label: 'R&B Code', width: 100, type: 'text' },
    { key: 'description', label: 'Description', width: 220, type: 'text' },
    { key: 'division', label: 'Division', width: 120, type: 'text' },
    { key: 'project', label: 'Project', width: 140, type: 'text' },
    { key: 'rb_hrs_per_wk', label: 'Hours/Week', width: 105, type: 'number' },
    { key: 'rb_nb_days', label: 'Nb Days', width: 90, type: 'number' },
    { key: 'lodging_daily', label: 'Lodging Daily', width: 115, type: 'number' },
    { key: 'travel_daily', label: 'Travel Daily', width: 110, type: 'number' },
    { key: 'rb_ds_addl_daily', label: 'Addl Daily', width: 100, type: 'number' },
    { key: 'rb_ds_total_daily', label: 'Total Daily', width: 105, type: 'number' },
    { key: 'addl_weekly', label: 'Addl Weekly', width: 110, type: 'number' },
    { key: 'total_weekly', label: 'Total Weekly', width: 115, type: 'number' },
    { key: 'rb_notes', label: 'Notes', width: 200, type: 'text' },
  ],
};

SECTION_COLUMNS['Working Week - At Site'] = SECTION_COLUMNS['Working Week - Pre-Site'];
SECTION_COLUMNS['Working Week - Demobilization'] = SECTION_COLUMNS['Working Week - Pre-Site'];
SECTION_COLUMNS['Field Supervision'] = SECTION_COLUMNS['Project Support Staff'];
SECTION_COLUMNS['Field Labour'] = [
  { key: 'combined_team_ratio', label: 'Team Ratio', width: 100, type: 'number' },
  ...SECTION_COLUMNS['Project Support Staff'],
];
SECTION_COLUMNS['Shop Labour'] = SECTION_COLUMNS['Field Labour'];
SECTION_COLUMNS['R&B Foremen & GF - Hourly'] = SECTION_COLUMNS['R&B Trades - Hourly'];

const fmt = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const n = parseFloat(val);
  if (isNaN(n)) return val;
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtCurrency = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const n = parseFloat(val);
  if (isNaN(n)) return val;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const LabourRatesBankView = () => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('cbLabourRatesBank', 'global', true);
  const [items, setItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('all');
  const [search, setSearch] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [toast, setToast] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showHelp, setShowHelp] = useState(false);
  const tableRef = useRef(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [itemsRes, sectionsRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/cost-breakdown/rates-bank'),
        authenticatedApiCall(instance, accounts, 'GET', '/cost-breakdown/rates-bank/sections'),
      ]);
      if (itemsRes.data?.success) setItems(itemsRes.data.data || []);
      if (sectionsRes.data?.success) setSections(sectionsRes.data.data || []);
    } catch (err) {
      console.error('Error fetching rates bank:', err);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const uniqueDivisions = useMemo(() => {
    const divs = new Set();
    items.forEach(it => { if (it.division) divs.add(it.division); });
    return Array.from(divs).sort();
  }, [items]);

  const uniqueProjects = useMemo(() => {
    const projs = new Set();
    items.forEach(it => { if (it.project) projs.add(it.project); });
    return Array.from(projs).sort();
  }, [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (activeSection !== 'all') list = list.filter(r => r.section === activeSection);
    if (divisionFilter) list = list.filter(r => r.division === divisionFilter);
    if (projectFilter) list = list.filter(r => r.project === projectFilter);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter(r =>
        (r.description || '').toLowerCase().includes(s) ||
        (r.account_no || '').toLowerCase().includes(s) ||
        (r.trade || '').toLowerCase().includes(s) ||
        (r.section || '').toLowerCase().includes(s) ||
        (r.division || '').toLowerCase().includes(s) ||
        (r.ds_notes || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [items, activeSection, divisionFilter, projectFilter, search]);

  const currentColumns = useMemo(() => {
    if (activeSection === 'all') {
      return [
        { key: 'section', label: 'Section', width: 200, type: 'text' },
        { key: 'account_no', label: 'Account #', width: 110, type: 'text' },
        { key: 'description', label: 'Description', width: 260, type: 'text' },
        { key: 'trade', label: 'Trade', width: 150, type: 'text' },
        { key: 'division', label: 'Division', width: 120, type: 'text' },
        { key: 'project', label: 'Project', width: 140, type: 'text' },
        { key: 'ds_straight_time', label: 'DS Straight', width: 105, type: 'number', group: 'Day Shift' },
        { key: 'ds_overtime_t15', label: 'DS OT', width: 90, type: 'number', group: 'Day Shift' },
        { key: 'ds_double_time', label: 'DS Dbl', width: 90, type: 'number', group: 'Day Shift' },
        { key: 'ds_site_blended', label: 'DS Site Blend', width: 115, type: 'number', group: 'Day Shift' },
        { key: 'ds_notes', label: 'DS Notes', width: 180, type: 'text' },
        { key: 'ns_straight_time', label: 'NS Straight', width: 105, type: 'number', group: 'Night Shift' },
        { key: 'ns_site_blended', label: 'NS Site Blend', width: 115, type: 'number', group: 'Night Shift' },
        { key: 'ns_notes', label: 'NS Notes', width: 180, type: 'text' },
      ];
    }
    return SECTION_COLUMNS[activeSection] || SECTION_COLUMNS['Project Support Staff'];
  }, [activeSection]);

  const columnGroups = useMemo(() => {
    const groups = [];
    let currentGroup = null;
    currentColumns.forEach((col, idx) => {
      if (col.group !== currentGroup) {
        currentGroup = col.group;
        groups.push({ name: col.group || null, startIdx: idx, count: 1 });
      } else {
        groups[groups.length - 1].count++;
      }
    });
    return groups;
  }, [currentColumns]);

  const startEdit = (item) => {
    setEditingId(item.id);
    const form = {};
    currentColumns.forEach(col => { form[col.key] = item[col.key] ?? ''; });
    setEditForm(form);
  };

  const handleSave = async () => {
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/cost-breakdown/rates-bank/${editingId}`, editForm);
      setEditingId(null);
      fetchData();
      showToast('Saved');
    } catch { showToast('Failed to save', 'error'); }
  };

  const handleAdd = async () => {
    try {
      const newRow = { section: activeSection === 'all' ? 'Uncategorized' : activeSection };
      await authenticatedApiCall(instance, accounts, 'POST', '/cost-breakdown/rates-bank', newRow);
      fetchData();
      showToast('Row added');
    } catch { showToast('Failed to add', 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/cost-breakdown/rates-bank/${id}`);
      fetchData();
      showToast('Deleted');
    } catch { showToast('Failed to delete', 'error'); }
  };

  const handleBulkDelete = async () => {
    if (!selectedRows.size) return;
    if (!window.confirm(`Delete ${selectedRows.size} selected rows?`)) return;
    try {
      await authenticatedApiCall(instance, accounts, 'POST', '/cost-breakdown/rates-bank/bulk-delete', { ids: Array.from(selectedRows) });
      setSelectedRows(new Set());
      fetchData();
      showToast(`Deleted ${selectedRows.size} rows`);
    } catch { showToast('Failed to delete', 'error'); }
  };

  const handleExport = async () => {
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', '/cost-breakdown/rates-bank/export', null, 'blob');
      const url = window.URL.createObjectURL(new Blob([result.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'labour_rates_bank.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Exported');
    } catch { showToast('Failed to export', 'error'); }
  };

  const toggleRow = (id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === filtered.length) setSelectedRows(new Set());
    else setSelectedRows(new Set(filtered.map(r => r.id)));
  };

  const totalRecords = items.length;
  const totalSections = sections.length;
  const avgRate = useMemo(() => {
    const rates = items.filter(it => it.ds_site_blended).map(it => parseFloat(it.ds_site_blended));
    if (!rates.length) return 0;
    return rates.reduce((a, b) => a + b, 0) / rates.length;
  }, [items]);

  const groupStyle = (color) => ({
    background: color || '#f8fafc',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '4px 8px',
    textAlign: 'center',
    borderBottom: '2px solid rgba(255,255,255,0.3)',
  });

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f0f4f8', fontFamily: "'Inter', -apple-system, sans-serif" }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 10000, padding: '12px 24px',
          borderRadius: '10px', color: '#fff', fontSize: '0.85rem', fontWeight: 600,
          background: toast.type === 'error' ? '#ef4444' : '#10b981',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)', animation: 'slideIn 0.3s ease',
        }}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
        padding: '20px 28px', flexShrink: 0,
        borderBottom: '3px solid #2563eb',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.02em' }}>
              💰 Labour Rates Bank
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
              Consolidated labour rates across all estimation categories
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <ActiveViewers
              viewers={viewers}
              isConnected={isConnected}
              connectionState={connectionState}
              maxVisible={4}
              showConnectionStatus={true}
            />
            <button onClick={() => setShowHelp(true)} style={{
              padding: '8px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.08)', color: '#e2e8f0', cursor: 'pointer', fontSize: '0.8rem',
            }}>❓ Help</button>
            <button onClick={handleExport} style={{
              padding: '8px 16px', borderRadius: '8px', border: 'none',
              background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff',
              cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
            }}>📥 Export</button>
            <button onClick={handleAdd} style={{
              padding: '8px 16px', borderRadius: '8px', border: 'none',
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff',
              cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
            }}>+ Add Rate</button>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
          {[
            { label: 'Total Records', value: totalRecords, icon: '📊', color: '#2563eb' },
            { label: 'Sections', value: totalSections, icon: '📁', color: '#7c3aed' },
            { label: 'Avg Site Blend Rate', value: fmtCurrency(avgRate), icon: '💵', color: '#059669' },
            { label: 'Showing', value: filtered.length, icon: '🔍', color: '#ea580c' },
          ].map((card, i) => (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.06)', borderRadius: '10px', padding: '12px 16px',
              border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.2rem' }}>{card.icon}</span>
                <div>
                  <div style={{ fontSize: '0.65rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{card.label}</div>
                  <div style={{ fontSize: '1.15rem', color: '#fff', fontWeight: 700 }}>{card.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Section Tabs */}
      <div style={{
        padding: '12px 28px', background: '#fff', borderBottom: '1px solid #e2e8f0',
        overflowX: 'auto', display: 'flex', gap: '6px', flexShrink: 0,
      }}>
        <button
          onClick={() => setActiveSection('all')}
          style={{
            padding: '6px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer',
            fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap', transition: 'all 0.2s',
            background: activeSection === 'all' ? '#1e293b' : '#f1f5f9',
            color: activeSection === 'all' ? '#fff' : '#475569',
          }}
        >
          All ({totalRecords})
        </button>
        {sections.map(sec => {
          const config = SECTION_CONFIG[sec.section] || { icon: '📄', color: '#6b7280', shortLabel: sec.section };
          const isActive = activeSection === sec.section;
          return (
            <button
              key={sec.section}
              onClick={() => setActiveSection(sec.section)}
              style={{
                padding: '6px 14px', borderRadius: '20px', border: 'none', cursor: 'pointer',
                fontSize: '0.73rem', fontWeight: 600, whiteSpace: 'nowrap', transition: 'all 0.2s',
                background: isActive ? config.gradient : '#f1f5f9',
                color: isActive ? '#fff' : '#475569',
                boxShadow: isActive ? `0 2px 8px ${config.color}40` : 'none',
              }}
            >
              {config.icon} {config.shortLabel} ({sec.count})
            </button>
          );
        })}
      </div>

      {/* Filters Bar */}
      <div style={{
        padding: '10px 28px', background: '#fff', borderBottom: '1px solid #e2e8f0',
        display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap',
      }}>
        <div style={{ position: 'relative', flex: '1 1 250px', maxWidth: '350px' }}>
          <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.85rem', color: '#9ca3af' }}>🔍</span>
          <input
            type="text" placeholder="Search rates..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', padding: '8px 12px 8px 32px', borderRadius: '8px', border: '1px solid #d1d5db',
              fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>
        <select
          value={divisionFilter} onChange={e => setDivisionFilter(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db',
            fontSize: '0.82rem', background: '#fff', cursor: 'pointer', color: divisionFilter ? '#1e293b' : '#9ca3af',
          }}
        >
          <option value="">All Divisions</option>
          {uniqueDivisions.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select
          value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
          style={{
            padding: '8px 12px', borderRadius: '8px', border: '1px solid #d1d5db',
            fontSize: '0.82rem', background: '#fff', cursor: 'pointer', color: projectFilter ? '#1e293b' : '#9ca3af',
          }}
        >
          <option value="">All Projects</option>
          {uniqueProjects.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        {selectedRows.size > 0 && (
          <button onClick={handleBulkDelete} style={{
            padding: '7px 14px', borderRadius: '8px', border: 'none',
            background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
          }}>
            🗑️ Delete {selectedRows.size}
          </button>
        )}
        <div style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#6b7280' }}>
          {filtered.length} record{filtered.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Data Grid */}
      <div ref={tableRef} style={{ flex: 1, overflow: 'auto', padding: '0 12px 12px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <div style={{ fontSize: '1.5rem', color: '#6b7280' }}>⏳ Loading rates...</div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px', color: '#9ca3af' }}>
            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📭</div>
            <h3 style={{ margin: '0 0 8px', color: '#6b7280' }}>No rates found</h3>
            <p style={{ fontSize: '0.85rem' }}>Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', marginTop: '12px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                {columnGroups.some(g => g.name) && (
                  <tr>
                    <th style={{ ...groupStyle('#64748b'), width: '40px' }}></th>
                    {columnGroups.map((g, i) => (
                      <th
                        key={i}
                        colSpan={g.count}
                        style={groupStyle(
                          g.name === 'Day Shift' ? '#2563eb' :
                          g.name === 'Night Shift' ? '#7c3aed' :
                          '#64748b'
                        )}
                      >
                        {g.name || ''}
                      </th>
                    ))}
                    <th style={{ ...groupStyle('#64748b'), width: '80px' }}></th>
                  </tr>
                )}
                <tr style={{ background: '#f8fafc' }}>
                  <th style={{
                    padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid #e2e8f0',
                    position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2, width: '40px',
                  }}>
                    <input type="checkbox" checked={selectedRows.size === filtered.length && filtered.length > 0}
                      onChange={toggleAll} style={{ cursor: 'pointer' }} />
                  </th>
                  {currentColumns.map(col => (
                    <th key={col.key} style={{
                      padding: '10px 8px', textAlign: col.type === 'number' ? 'right' : 'left',
                      borderBottom: '2px solid #e2e8f0', fontWeight: 700, color: '#374151',
                      fontSize: '0.72rem', whiteSpace: 'nowrap', position: 'sticky', top: 0,
                      background: '#f8fafc', zIndex: 2, minWidth: col.width,
                    }}>
                      {col.label}
                    </th>
                  ))}
                  <th style={{
                    padding: '10px 8px', textAlign: 'center', borderBottom: '2px solid #e2e8f0',
                    position: 'sticky', top: 0, background: '#f8fafc', zIndex: 2, width: '80px',
                    fontWeight: 700, color: '#374151', fontSize: '0.72rem',
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, rowIdx) => {
                  const isEditing = editingId === row.id;
                  const isSelected = selectedRows.has(row.id);
                  const secConfig = SECTION_CONFIG[row.section] || { color: '#6b7280' };
                  const isCombinedRow = (row.description || '').toLowerCase().includes('combined team');
                  const isCompositeRow = (row.description || '').toLowerCase().includes('composite crew');

                  return (
                    <tr key={row.id} style={{
                      background: isSelected ? '#eff6ff' : isCombinedRow ? '#fef3c7' : isCompositeRow ? '#e0e7ff' : rowIdx % 2 ? '#fafafa' : '#fff',
                      borderBottom: '1px solid #f1f5f9',
                      transition: 'background 0.15s',
                    }}
                      onMouseEnter={e => { if (!isSelected && !isCombinedRow && !isCompositeRow) e.currentTarget.style.background = '#f0f9ff'; }}
                      onMouseLeave={e => { if (!isSelected && !isCombinedRow && !isCompositeRow) e.currentTarget.style.background = rowIdx % 2 ? '#fafafa' : '#fff'; }}
                    >
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <input type="checkbox" checked={isSelected} onChange={() => toggleRow(row.id)} style={{ cursor: 'pointer' }} />
                      </td>
                      {currentColumns.map(col => (
                        <td key={col.key} style={{
                          padding: '6px 8px', textAlign: col.type === 'number' ? 'right' : 'left',
                          fontWeight: col.key === 'description' || col.key === 'section' ? 600 : 400,
                          color: col.key === 'section' ? secConfig.color : '#374151',
                          whiteSpace: 'nowrap',
                        }}>
                          {isEditing && col.key !== 'section' ? (
                            <input
                              type={col.type === 'number' ? 'number' : 'text'}
                              value={editForm[col.key] ?? ''}
                              onChange={e => setEditForm({ ...editForm, [col.key]: e.target.value })}
                              step={col.type === 'number' ? '0.01' : undefined}
                              style={{
                                width: '100%', padding: '3px 6px', borderRadius: '4px',
                                border: '1px solid #93c5fd', fontSize: '0.78rem',
                                textAlign: col.type === 'number' ? 'right' : 'left',
                                outline: 'none', boxSizing: 'border-box',
                              }}
                            />
                          ) : (
                            col.type === 'number' ? fmt(row[col.key]) :
                            col.key === 'section' ? (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                background: `${secConfig.color}12`, padding: '2px 8px', borderRadius: '12px',
                                fontSize: '0.72rem',
                              }}>
                                {(SECTION_CONFIG[row.section] || {}).icon} {(SECTION_CONFIG[row.section] || {}).shortLabel || row.section}
                              </span>
                            ) : (row[col.key] || '')
                          )}
                        </td>
                      ))}
                      <td style={{ padding: '6px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {isEditing ? (
                          <>
                            <button onClick={handleSave} style={{
                              padding: '3px 8px', borderRadius: '4px', border: 'none',
                              background: '#10b981', color: '#fff', cursor: 'pointer', fontSize: '0.72rem', marginRight: '4px',
                            }}>✓</button>
                            <button onClick={() => setEditingId(null)} style={{
                              padding: '3px 8px', borderRadius: '4px', border: 'none',
                              background: '#6b7280', color: '#fff', cursor: 'pointer', fontSize: '0.72rem',
                            }}>✕</button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => startEdit(row)} style={{
                              padding: '3px 8px', borderRadius: '4px', border: 'none',
                              background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: '0.72rem', marginRight: '4px',
                            }}>✏️</button>
                            <button onClick={() => handleDelete(row.id)} style={{
                              padding: '3px 8px', borderRadius: '4px', border: 'none',
                              background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: '0.72rem',
                            }}>🗑️</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', justifyContent: 'center', alignItems: 'center',
        }} onClick={() => setShowHelp(false)}>
          <div style={{
            background: '#fff', borderRadius: '16px', padding: '32px', maxWidth: '600px',
            width: '90%', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '1.3rem', color: '#1e293b' }}>💰 Labour Rates Bank</h2>
              <ListenButton compact guideId="labour-rates-bank-help" title="Labour Rates Bank Help" />
            </div>
            <HelpWithVideoTour guideId="labour-rates-bank-help" title="Labour Rates Bank Help" accent="#1e293b">
            <p style={{ color: '#475569', lineHeight: 1.7, fontSize: '0.9rem' }}>
              The Labour Rates Bank is a centralized repository for all labour rate data used in estimation.
              It consolidates rates across multiple categories:
            </p>
            <div style={{ margin: '16px 0' }}>
              {Object.entries(SECTION_CONFIG).map(([name, cfg]) => (
                <div key={name} style={{
                  display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 0',
                  borderBottom: '1px solid #f1f5f9',
                }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '28px', height: '28px', borderRadius: '8px', background: cfg.gradient, fontSize: '0.85rem',
                  }}>{cfg.icon}</span>
                  <span style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 500 }}>{name}</span>
                </div>
              ))}
            </div>
            <p style={{ color: '#475569', lineHeight: 1.7, fontSize: '0.85rem' }}>
              Use the <strong>Division</strong> and <strong>Project</strong> filters to narrow down rates.
              Click section tabs to view category-specific columns. All rates support inline editing.
            </p>
            </HelpWithVideoTour>
            <button onClick={() => setShowHelp(false)} style={{
              marginTop: '16px', padding: '10px 24px', borderRadius: '8px', border: 'none',
              background: '#1e293b', color: '#fff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
            }}>Got it</button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default LabourRatesBankView;
